import { forwardRef, useState, useEffect, useRef, useImperativeHandle, type ReactNode } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { Popover } from '@mui/material';
import { PiCalendarFill, PiXBold } from 'react-icons/pi';
import { useInstance } from '../../Hooks/useInstance';
import { cn } from '../../lib/utils';

interface DatePickerProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'error'> {
    label?: string;
    value?: string | Date | null;
    onChange?: (value: any) => void;
    error?: string | { message?: string } | null;
    helper?: string;
    fullWidth?: boolean;
    clearable?: boolean;
    tileDisabled?: (props: { date: Date; view: string }) => boolean;
    tileClassName?: (props: { date: Date; view: string }) => string | null | undefined;
    tileContent?: (props: { date: Date; view: string }) => ReactNode;
    /** Rendered inside the field, right after the formatted date (e.g. a status pill) */
    adornment?: ReactNode;
    /** Rendered under the calendar grid (e.g. a colour legend) */
    calendarFooter?: ReactNode;
    /** Shows a required marker next to the label */
    required?: boolean;
}

/**
 * Premium DatePicker — Unified calendar-based date selection.
 * Integrates with react-hook-form via standard name/onChange/value props.
 */
export const DatePicker = forwardRef<HTMLInputElement, DatePickerProps>(
    ({ label, value, onChange, error, helper, fullWidth = true, clearable = false, className, id, disabled, placeholder = 'Select Date', tileDisabled, tileClassName, tileContent, adornment, calendarFooter, required, ...rest }, ref) => {
        const { dateUtils, DATE_FORMATS } = useInstance();
        const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
        const inputRef = useRef<HTMLInputElement>(null);

        // Merge refs: expose inputRef to parent (RHF)
        useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

        // Track internal state to support uncontrolled use-cases (like react-hook-form's register)
        const [internalValue, setInternalValue] = useState<string>(() => {
            const initial = value ?? rest.defaultValue ?? '';
            return typeof initial === 'string' ? initial : (initial instanceof Date ? dateUtils.toSchoolDateISO(initial) : '');
        });

        // Sync internal state when controlled value changes (e.g. form hydration)
        useEffect(() => {
            if (value !== undefined) {
                const formatted = typeof value === 'string' ? value : (value instanceof Date ? dateUtils.toSchoolDateISO(value) : '');
                setInternalValue(formatted || '');
            }
        }, [value, dateUtils]);

        // Support persistence on remount (e.g. Back button in stepper)
        useEffect(() => {
            // If the input has a value but our state is empty, sync it.
            // RHF might have hydrated the input value before we mounted.
            if (inputRef.current?.value && !internalValue) {
                setInternalValue(inputRef.current.value);
            }
        }, []);

        const handleClick = (event: React.MouseEvent<HTMLElement>) => {
            if (!disabled) setAnchorEl(event.currentTarget);
        };

        const handleClose = () => setAnchorEl(null);

        const handleDateChange = (val: any) => {
            // Handle various react-calendar outputs (Date, Date[], or null)
            const selectedDate = Array.isArray(val) ? val[0] : val;

            if (selectedDate instanceof Date) {
                const isoDate = dateUtils.toSchoolDateISO(selectedDate);
                setInternalValue(isoDate); // Update internal state immediately for visual feedback

                if (onChange) {
                    // Create a synthetic event for react-hook-form/register compatibility
                    onChange({
                        target: {
                            name: rest.name,
                            value: isoDate,
                        },
                    } as any);
                }
            } else if (selectedDate === null) {
                setInternalValue(''); // Clear internal state
                if (onChange) {
                    onChange({
                        target: {
                            name: rest.name,
                            value: null, // Pass null for cleared date
                        },
                    } as any);
                }
            }
            handleClose();
        };

        const handleClear = (e: React.MouseEvent) => {
            e.stopPropagation(); // Avoid opening the popover if clicked on inline clear
            setInternalValue('');
            if (onChange) {
                onChange({
                    target: {
                        name: rest.name,
                        value: null,
                    },
                } as any);
            }
            handleClose();
        };

        const open = Boolean(anchorEl);
        const errorMsg = typeof error === 'string' ? error : (error as any)?.message;
        const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');
        const dateValue = internalValue ? new Date(internalValue + 'T12:00:00') : null;

        return (
            <div className={cn('flex flex-col gap-1.5', fullWidth && 'w-full')}>
                {label && (
                    <label htmlFor={inputId} className="text-xs font-bold text-slate-600 select-none">
                        {label}
                        {required && <span className="text-red-500 ml-0.5">*</span>}
                    </label>
                )}

                <div
                    id={inputId}
                    onClick={handleClick}
                    className={cn(
                        'flex h-10 w-full items-center justify-between rounded-xl border px-3 py-2 text-sm transition-all duration-300 cursor-pointer',
                        'bg-white text-slate-700 font-semibold',
                        open ? 'ring-4 ring-teal-primary/20 border-teal-primary shadow-lg shadow-teal-500/5' : 'border-slate-200 shadow-sm',
                        errorMsg ? 'border-red-400 ring-red-500/10' : '',
                        disabled ? 'bg-slate-50 text-slate-400 cursor-not-allowed opacity-50' : 'hover:border-slate-300 hover:bg-slate-50/50',
                        className
                    )}
                >
                    <div className="flex items-center gap-2 overflow-hidden">
                        <span className={cn('truncate', !internalValue && 'text-slate-300 font-normal italic')}>
                            {internalValue ? dateUtils.formatDate(internalValue, DATE_FORMATS.NUMERIC_DATE) : placeholder}
                        </span>
                        {adornment}
                    </div>
                    <div className="flex items-center gap-2 shrink-0 ml-2">
                        {clearable && internalValue && !disabled && (
                            <button
                                type="button"
                                onClick={handleClear}
                                className="p-1 rounded-full hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors"
                                title="Clear date"
                            >
                                <PiXBold size={14} />
                            </button>
                        )}
                        <PiCalendarFill className={cn('transition-colors duration-300', open ? 'text-teal-600' : 'text-slate-400')} size={18} />
                    </div>
                </div>

                <Popover
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    disableScrollLock // Prevent UI jump when opening in modals
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    slotProps={{
                        paper: {
                            className: 'mt-2 p-2 rounded-2xl border border-slate-100/50 shadow-2xl backdrop-blur-2xl bg-white/95 animate-in fade-in zoom-in-95 duration-200',
                            style: { borderRadius: '20px', zIndex: 30000 } // Ensure it's above modals and standard UI
                        }
                    }}
                    sx={{ zIndex: 30000 }} // Explicitly set z-index on the wrapper as well
                >
                    <div className="flex flex-col gap-2">
                        <Calendar
                            value={dateValue}
                            onChange={handleDateChange}
                            tileDisabled={tileDisabled}
                            tileClassName={tileClassName}
                            tileContent={tileContent}
                            className="!border-none !font-sans premium-calendar grayscale-[0.2]"
                        />
                        {calendarFooter}
                        {clearable && internalValue && (
                            <div className="px-2 pb-2">
                                <button
                                    type="button"
                                    onClick={handleClear}
                                    className="w-full py-2 text-xs font-bold text-rose-500 hover:bg-rose-50 rounded-xl transition-colors border border-rose-100/50"
                                >
                                    Clear Date
                                </button>
                            </div>
                        )}
                    </div>
                </Popover>

                {errorMsg && <p className="text-[10px] font-bold text-red-500 mt-0.5 ml-1">{errorMsg}</p>}
                {!errorMsg && helper && <p className="text-[10px] text-slate-400 mt-0.5 ml-1">{helper}</p>}

                {/*
                   Hidden input for RHF ref registration.
                   We specifically MUST assign the local ref here so we can read its value for persistence.
                */}
                <input
                    ref={inputRef}
                    type="hidden"
                    name={rest.name}
                    value={internalValue}
                    {...rest}
                />
            </div>
        );
    }
);

DatePicker.displayName = 'DatePicker';
